import { clientApi } from "@/utils/api";
import { TCreateCaseRequestDto } from "@/api/services/main/case/dtos/create-case.dto";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { TGetCaseResDto } from "@/api/services/main/case/dtos/get-case.dto";
import { TGetCaseCountResDto } from "@/api/services/main/case/dtos/get-case-count.dto";
import { TCheckCaseDuplicateResDto } from "@/api/services/main/case/dtos/check-case-duplicate.dto";
import { useMemo } from "react";
import { TGetCaseMatchesReqDto } from "@/api/services/main/case/dtos/get-case-matches.dto";
import { createUrlWithParams } from "@/utils/url";
import { getFilenameFromContentDisposition } from "@/utils/export";
import { TCreateCaseCommentReqDto, TCreateCaseCommentResDto } from "@/api/services/main/case/dtos/create-case-comment.dto";
import { TGetCaseCommentsResDto } from "@/api/services/main/case/dtos/get-case-comment.dto";
import { TDeleteCaseCommentResDto } from "@/api/services/main/case/dtos/delete-case-comment.dto";
import { TUpdateCaseRequestDto } from "@/api/services/main/case/dtos/update-case.dto";
import { TGetCaseFilterOptionsResDto, TGetCaseListReqDto, TGetCaseListResDto } from "@/api/services/main/case/dtos/get-case-list.dto";
import { CACHED_TIME } from "@/constants/common";
import { TUpdateCaseMatchRequestDto, TUpdateCaseMatchResDto } from "@/api/services/main/case/dtos/update-case-match.dto";
import { TCreateCaseMatchesRequestDto, TCreateCaseMatchesResponseDto } from "@/api/services/main/case/dtos/create-case-matches.dto";

export const fetchCaseListApi = async (params: TGetCaseListReqDto): Promise<TGetCaseListResDto> => {
  const { data } = await clientApi.get<TGetCaseListResDto>("/cases", { params });
  return data;
};

export const useCaseList = (params: TGetCaseListReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.CASE.FETCH_LIST, params],
    queryFn: () => fetchCaseListApi(params),
  });
};

export const fetchCaseFilterOptionsApi = async (): Promise<TGetCaseFilterOptionsResDto> => {
  const { data } = await clientApi.get<TGetCaseFilterOptionsResDto>("/cases/filter-options");
  return data;
};

export const useCaseFilterOptions = () => {
  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.CASE.FETCH_FILTER_OPTIONS],
    queryFn: () => fetchCaseFilterOptionsApi(),
    staleTime: CACHED_TIME,
  });

  const managementNumberOptions = useMemo(() => (data?.data?.managementNumbers ?? []).map((v) => ({ label: v, value: v })), [data]);
  const caseNameOptions = useMemo(() => (data?.data?.caseNames ?? []).map((v) => ({ label: v, value: v })), [data]);
  const jobNameOptions = useMemo(() => (data?.data?.jobNames ?? []).map((v) => ({ label: v, value: v })), [data]);
  const companyNameOptions = useMemo(() => (data?.data?.companyNames ?? []).map((v) => ({ label: v, value: v })), [data]);
  const recruitmentNumberOptions = useMemo(
    () => (data?.data?.recruitmentNumbers ?? []).map((v) => ({ label: `${v}人`, value: String(v) })),
    [data],
  );

  return { managementNumberOptions, caseNameOptions, jobNameOptions, companyNameOptions, recruitmentNumberOptions, ...rest };
};

export const createCase = async (caseDataRequest: TCreateCaseRequestDto) => {
  const { data } = await clientApi.post("/cases", caseDataRequest);
  return data;
};

export const updateCaseApi = async (id: number | string, payload: TUpdateCaseRequestDto) => {
  const { data } = await clientApi.put(`/cases/${id}`, payload);
  return data;
};

export const fetchCaseInfo = async (id: number | string): Promise<TGetCaseResDto> => {
  const { data } = await clientApi.get(`/cases/${id}`);
  return data;
};

export const useCaseInfo = (id: number | string) => {
  const { caseId } = useParams();

  const fetchId = id ?? caseId;

  const isValidId = useMemo(() => {
    if (!fetchId) return false;
    const idNumber = Number(fetchId);
    return !isNaN(idNumber) && Number.isInteger(idNumber) && idNumber > 0;
  }, [fetchId]);

  return useQuery({
    queryKey: [QUERY_KEYS.CASE.FETCH_LIST, fetchId],
    queryFn: async () => {
      return fetchId ? await fetchCaseInfo(fetchId) : undefined;
    },
    enabled: isValidId,
  });
};

export const fetchCaseCountApi = async (): Promise<TGetCaseCountResDto> => {
  const { data } = await clientApi.get<TGetCaseCountResDto>("/cases/count");
  return data;
};

export const useCaseCount = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.CASE.FETCH_COUNT],
    queryFn: async () => {
      return await fetchCaseCountApi();
    },
  });
};

export const fetchCheckCaseDuplicateApi = async (jobId: number, caseName: string, caseId?: number): Promise<TCheckCaseDuplicateResDto> => {
  const params = { jobId: String(jobId), caseName, ...(caseId && { id: String(caseId) }) };
  const url = createUrlWithParams("/cases/check-case-duplicate", params);

  const { data } = await clientApi.get<TCheckCaseDuplicateResDto>(url);
  return data;
};

export const fetchCaseMatches = async (caseId: number | string, query: TGetCaseMatchesReqDto) => {
  const url = createUrlWithParams(`/cases/${caseId}/case-matches`, query);

  const { data } = await clientApi.get(url);
  return data;
};

export const useCaseMatches = (caseId: number | string, query: TGetCaseMatchesReqDto, enabled: boolean = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.CASE.FETCH_CASE_MATCHES, caseId, query],
    queryFn: async () => {
      return caseId ? await fetchCaseMatches(caseId, query) : undefined;
    },
    enabled: Boolean(caseId) && enabled,
  });
};

export const updateCaseMatchApi = async (
  caseId: number | string,
  caseMatchId: number | string,
  payload: TUpdateCaseMatchRequestDto,
): Promise<TUpdateCaseMatchResDto> => {
  const { data } = await clientApi.put<TUpdateCaseMatchResDto>(`/cases/${caseId}/case-matches/${caseMatchId}`, payload);
  return data;
};

export const deleteCaseMatchApi = async (caseId: number | string, caseMatchId: number | string) => {
  const { data } = await clientApi.delete(`/cases/${caseId}/case-matches/${caseMatchId}`);
  return data;
};

export const createCaseMatchesApi = async (
  caseId: number | string,
  payload: TCreateCaseMatchesRequestDto,
): Promise<TCreateCaseMatchesResponseDto> => {
  const { data } = await clientApi.post<TCreateCaseMatchesResponseDto>(`/cases/${caseId}/case-matches`, payload);
  return data;
};

export const createCaseCommentApi = async (caseId: number, payload: TCreateCaseCommentReqDto): Promise<TCreateCaseCommentResDto> => {
  const rs = await clientApi.post<TCreateCaseCommentResDto>(`/cases/${caseId}/comments`, payload);
  return rs.data;
};

export const deleteCaseCommentApi = async (caseId: number, commentId: number): Promise<TDeleteCaseCommentResDto> => {
  const rs = await clientApi.delete<TDeleteCaseCommentResDto>(`/cases/${caseId}/comments/${commentId}`);
  return rs.data;
};

export const fetchCaseCommentsApi = async (id: string | number): Promise<TGetCaseCommentsResDto> => {
  const { data } = await clientApi.get<TGetCaseCommentsResDto>(`/cases/${id}/comments`);
  return data;
};

export const useCaseCommentsList = (id?: number) => {
  const { caseId } = useParams();

  const fetchId = id ?? caseId;
  return useQuery({
    queryKey: [QUERY_KEYS.CASE_COMMENT.FETCH_LIST, fetchId],
    queryFn: async () => {
      return fetchId ? await fetchCaseCommentsApi(fetchId) : undefined;
    },
    enabled: Boolean(fetchId),
  });
};

/**
 * Download all resumes for all case matches in a case (no pagination)
 */
export const downloadAllResumesByCaseApi = async (caseId: number | string) => {
  return await clientApi.get(`/cases/${caseId}/download-all-resumes`, {
    responseType: "blob",
  });
};

/**
 * Export all talents of a case's case-matches as CSV.
 * Forwards filter params so the export reflects the user's current filter state.
 */
export const exportCaseMatchTalentsAsCsvApi = async (caseId: number | string, filters?: TGetCaseMatchesReqDto) => {
  const url = createUrlWithParams(`/cases/${caseId}/case-matches/export-talents`, filters ?? {});
  return await clientApi.get(url, { responseType: "blob" });
};

export type TCaseMatchDocType = "offer-acceptance" | "employment-notice";

export const exportCaseMatchDocxZipForMatchApi = async (
  caseId: number | string,
  caseMatchId: number | string,
  talentName?: string,
): Promise<{ blob: Blob; fileName: string }> => {
  const response = await clientApi.get(`/cases/${caseId}/case-matches/${caseMatchId}/export-docx-zip`, {
    responseType: "blob",
  });

  const fallback = talentName ? `採用通知表・内定承諾書_${talentName}.zip` : "採用通知表・内定承諾書.zip";
  const fileName = getFilenameFromContentDisposition(response.headers, fallback);

  return { blob: response.data, fileName };
};

export const exportCaseMatchDocxZipApi = async (
  caseId: number | string,
  docTypes: TCaseMatchDocType[],
): Promise<{ blob: Blob; fileName: string }> => {
  const url = createUrlWithParams(`/cases/${caseId}/case-matches/export-docx-zip`, { docTypes });
  const response = await clientApi.get(url, { responseType: "blob" });

  const fileName = getFilenameFromContentDisposition(response.headers, "採用通知表・内定承諾書.zip");

  return { blob: response.data, fileName };
};
